import React from "react";
import { List, useTable } from "@refinedev/antd";
import { Table, Space, Tag, Button, Tooltip, Popconfirm, message, Typography } from "antd";
import { IInvoice } from "../../interfaces";
import { useHashNavigation } from "../../hooks/useHashNavigation";
import { formatPrice } from "../../utils/priceUtils";
import { 
  EditOutlined, 
  EyeOutlined, 
  FilePdfOutlined,
  DeleteOutlined,
  MailOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  HomeOutlined,
  CalendarOutlined,
  MoonOutlined,
  UserOutlined as AntUserOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";

const { Text } = Typography;

export const InvoiceList: React.FC = () => {
  const { navigateWithHash } = useHashNavigation();

  const { tableProps } = useTable<IInvoice>({
    syncWithLocation: false, // Deaktiviere URL-Sync für client-seitige Sortierung
    sorters: {
      mode: "off", // Deaktiviere server-seitige Sortierung
    },
  });

  const getTypeTag = (type: string) => {
    switch (type) {
      case "storno":
        return <Tag color="red">Storno</Tag>;
      case "noshow":
        return <Tag color="orange">No-Show</Tag>;
      case "offer":
        return <Tag color="blue">Angebot</Tag>;
      default:
        return <Tag color="green">Standard</Tag>;
    }
  };

  const getPaymentStatusTag = (status: string) => {
    switch (status) {
      case "paid":
        return <Tag color="green" icon={<CheckCircleOutlined />}>Bezahlt</Tag>;
      case "partial":
        return <Tag color="orange" icon={<ClockCircleOutlined />}>Angezahlt</Tag>;
      case "unpaid":
      default:
        return <Tag color="red" icon={<ExclamationCircleOutlined />}>Offen</Tag>;
    }
  };

  const getEmailStatusTag = (invoice: IInvoice) => {
    if (invoice.email_sent) {
      return (
        <Tooltip title={`Gesendet am: ${dayjs(invoice.email_sent_at).format("DD.MM.YYYY HH:mm")}`}>
          <Tag color="green" icon={<MailOutlined />}>Gesendet</Tag>
        </Tooltip>
      );
    }
    return <Tag color="gray">Nicht gesendet</Tag>;
  };

  // Hilfsfunktion zum Extrahieren des Gesamtbetrags aus raw_json oder fallback
  const getTotalAmount = (invoice: IInvoice): number => {
    // Verwende totalAmount aus der API-Response
    if (typeof (invoice as any).totalAmount === 'number') {
      return (invoice as any).totalAmount;
    }
    
    // Versuche zuerst raw_json.calculation.totalWithVat
    if (invoice.raw_json?.calculation?.totalWithVat) {
      return invoice.raw_json.calculation.totalWithVat;
    }
    
    // Fallback auf totals.total
    if (invoice.totals?.total) {
      return invoice.totals.total;
    }
    
    // Fallback auf 0
    return 0;
  };

  // Hilfsfunktion zum Extrahieren der Unterkunft-Info
  const getAccommodationInfo = (invoice: IInvoice) => {
    // Verwende advert_id direkt aus der API-Response
    const advertId = (invoice as any).advert_id || invoice.accommodation?.advert_id;
    // Verwende advert_name aus der API-Response falls verfügbar
    const advertName = (invoice as any).advert_name;
    
    // Priorisiere advert_name aus der API
    if (advertName) {
      return {
        name: advertName,
        advertId: advertId
      };
    }
    
    // Versuche zuerst sender.name (da Sender die Unterkunft ist)
    if (invoice.sender?.name || invoice.sender?.company) {
      return {
        name: invoice.sender.company || invoice.sender.name,
        advertId: advertId
      };
    }
    
    // Fallback auf accommodation.name
    if (invoice.accommodation?.name) {
      return {
        name: invoice.accommodation.name,
        advertId: advertId
      };
    }
    
    return { name: advertId ? `Advert ${advertId}` : '-', advertId: advertId };
  };

  // Hilfsfunktion zum Abrufen der Gast-Info (müsste via customer_id geladen werden)
  const getGuestInfo = (invoice: IInvoice) => {
    // Priorisiere guest_name aus der API-Response
    const guestName = (invoice as any).guest_name;
    if (guestName) {
      return {
        name: guestName,
        email: null // Email nicht in der API verfügbar für guest_name
      };
    }
    
    // Falls guest-Objekt verfügbar ist
    if (invoice.guest?.name) {
      return {
        name: invoice.guest.name,
        email: invoice.guest.email
      };
    }
    
    // Falls nur customer_id verfügbar ist
    if ((invoice as any).customer_id) {
      return {
        name: `Kunde ${(invoice as any).customer_id}`,
        email: null
      };
    }
    
    return { name: '-', email: null };
  };

  // Hilfsfunktion zum Extrahieren der Aufenthaltsdaten
  const getStayInfo = (invoice: IInvoice) => {
    // Priorisiere arrival und departure aus der API-Response (bereits formatiert)
    const arrival = (invoice as any).arrival;
    const departure = (invoice as any).departure;
    const nights = (invoice as any).nights; // Nächte aus der API
    
    if (arrival && departure) {
      return {
        checkin: arrival, // Bereits im gewünschten Format
        checkout: departure, // Bereits im gewünschten Format
        nights: nights || null // Nächte aus API
      };
    }
    
    // Fallback auf raw_json.booking_timing
    if (invoice.raw_json?.booking_timing) {
      const { startDate, endDate, overnights } = invoice.raw_json.booking_timing;
      return {
        checkin: startDate ? dayjs.unix(startDate).format("DD.MM.YY") : null,
        checkout: endDate ? dayjs.unix(endDate).format("DD.MM.YY") : null,
        nights: overnights || null
      };
    }
    
    // Fallback auf accommodation Daten
    if (invoice.accommodation?.checkin && invoice.accommodation?.checkout) {
      return {
        checkin: dayjs(invoice.accommodation.checkin).format("DD.MM.YY"),
        checkout: dayjs(invoice.accommodation.checkout).format("DD.MM.YY"),
        nights: invoice.accommodation.nights || null
      };
    }
    
    return { checkin: null, checkout: null, nights: null };
  };

  // Hilfsfunktion zum Extrahieren der Gästezahl
  const getGuestCount = (invoice: IInvoice) => {
    if (invoice.raw_json?.booking_personal) {
      const { adults, children, dogs } = invoice.raw_json.booking_personal;
      let guestText = `${adults || 2} Erw.`;
      
      if (children?.count > 0) {
        guestText += `, ${children.count} Kind${children.count > 1 ? 'er' : ''}`;
      }
      
      if (dogs > 0) {
        guestText += `, ${dogs} Hund${dogs > 1 ? 'e' : ''}`;
      }
      
      return guestText;
    }
    
    // Fallback auf accommodation.guests
    if (invoice.accommodation?.guests) {
      return `${invoice.accommodation.guests} Gäste`;
    }
    
    return '-';
  };

  const resendEmail = (invoice: IInvoice) => {
    // API-Call zum erneuten Versenden der E-Mail
    message.loading('E-Mail wird gesendet...', 2);
    setTimeout(() => {
      message.success('E-Mail erfolgreich gesendet!');
    }, 2000);
  };

  const downloadPdf = async (invoice: IInvoice) => {
    try {
      message.loading('PDF wird generiert...', 0);
      
      const apiUrl = `${window.location.origin}/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=generatePdf&tx_buchungsmanager_frontend[id]=${invoice.id}`;
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/pdf'
        }
      });

      message.destroy(); // Remove loading message

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      // Check if response is actually a PDF
      const contentType = response.headers.get('Content-Type');
      if (!contentType?.includes('application/pdf')) {
        throw new Error('Antwort ist kein PDF-Format');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Create download link
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `${(invoice as any).fileName || `rechnung-${invoice.id}`}.pdf`;
      
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      message.success('PDF wurde heruntergeladen');
      
    } catch (error) {
      message.destroy(); // Remove loading message
      console.error('Error downloading PDF:', error);
      message.error(`Fehler beim PDF-Download: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
    }
  };

  return (
    <List
      title="Rechnungen"
      headerButtons={[
        <Button
          key="create"
          type="primary"
          icon={<EditOutlined />}
          onClick={() => navigateWithHash('/rechnungen/neu')}
        >
          Neue Rechnung
        </Button>,
      ]}
    >
      <Table {...tableProps} rowKey="id" size="small">
        <Table.Column
          dataIndex="fileName" 
          title="Rechnungsnummer"
          sorter={(a: any, b: any) => (a.fileName || '').localeCompare(b.fileName || '')}
          width={140}
          render={(value, record: IInvoice) => {
            const invoiceNumber = (record as any).fileName || value || 'Wird generiert';
            const isTemporary = invoiceNumber.startsWith('TEMP-');
            
            return (
              <Space direction="vertical" size={0}>
                <strong>{invoiceNumber}</strong>
                {isTemporary && (
                  <Text type="secondary" style={{ fontSize: '10px' }}>
                    (Temporär)
                  </Text>
                )}
              </Space>
            );
          }}
        />
        <Table.Column
          dataIndex="modifyDate"
          title="Datum"
          sorter={(a: any, b: any) => {
            const dateA = new Date(a.modifyDate || 0).getTime();
            const dateB = new Date(b.modifyDate || 0).getTime();
            return dateA - dateB;
          }}
          defaultSortOrder="descend"
          width={100}
          render={(value) => value ? dayjs(value).format("DD.MM.YYYY") : '-'}
        />
        <Table.Column
          dataIndex="type"
          title="Typ"
          width={100}
          sorter={(a: any, b: any) => (a.type || '').localeCompare(b.type || '')}
          render={(value, record: IInvoice) => {
            // Verwende den echten type Wert oder fallback
            const actualType = value || (record as any).type || 'standard';
            return getTypeTag(actualType);
          }}
          filters={[
            { text: "Standard", value: "standard" },
            { text: "Angebot", value: "offer" },
            { text: "Storno", value: "storno" },
            { text: "No-Show", value: "noshow" },
          ]}
          onFilter={(value, record: any) => {
            // Verwende den echten type Wert oder fallback zu 'standard'
            const actualType = record.type || (record as any).type || 'standard';
            console.log('Filter Debug - Value:', value, 'Record Type:', record.type, 'Actual Type:', actualType);
            
            // Spezielle Behandlung für 'standard' - könnte auch 'invoice' oder undefined sein
            if (value === 'standard') {
              return actualType === 'standard' || actualType === 'invoice' || !actualType || actualType === '';
            }
            
            return actualType === value;
          }}
        />
        <Table.Column
          title="Gast"
          width={220}
          sorter={(a: any, b: any) => {
            const guestA = a.guest_name || a.guest?.name || `Kunde ${a.customer_id}` || '';
            const guestB = b.guest_name || b.guest?.name || `Kunde ${b.customer_id}` || '';
            return guestA.localeCompare(guestB);
          }}
          render={(_, record: IInvoice) => {
            const guest = getGuestInfo(record);
            return (
              <div>
                <div><Text strong>{guest.name}</Text></div>
                {guest.email && (
                  <div><Text type="secondary" style={{ fontSize: '12px' }}>{guest.email}</Text></div>
                )}
              </div>
            );
          }}
        />
        <Table.Column
          title="Unterkunft & Advert ID"
          width={200}
          sorter={(a: any, b: any) => {
            const nameA = a.advert_name || a.sender?.company || a.sender?.name || `Advert ${a.advert_id}` || '';
            const nameB = b.advert_name || b.sender?.company || b.sender?.name || `Advert ${b.advert_id}` || '';
            return nameA.localeCompare(nameB);
          }}
          dataIndex="advert_id"
          render={(_, record: IInvoice) => {
            const accommodation = getAccommodationInfo(record);
            return (
              <div>
                <div>
                  <HomeOutlined style={{ marginRight: 4 }} />
                  <Text strong>{accommodation.name}</Text>
                </div>
                {accommodation.advertId && (
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Advert ID: {accommodation.advertId}
                    </Text>
                  </div>
                )}
              </div>
            );
          }}
        />
        <Table.Column
          title="Aufenthalt"
          width={160}
          render={(_, record: IInvoice) => {
            const stay = getStayInfo(record);
            const guests = getGuestCount(record);
            return (
              <div>
                {stay.checkin && stay.checkout ? (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                      <CalendarOutlined style={{ marginRight: 4, color: '#52c41a', fontSize: '10px' }} />
                      <Text style={{ fontSize: '12px' }}>
                        {stay.checkin} - {stay.checkout}
                      </Text>
                    </div>
                    {stay.nights && (
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                        <MoonOutlined style={{ marginRight: 4, color: '#1890ff', fontSize: '10px' }} />
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          {stay.nights} Nächte
                        </Text>
                      </div>
                    )}
                  </div>
                ) : (
                  <Text type="secondary">-</Text>
                )}
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <AntUserOutlined style={{ marginRight: 4, color: '#fa8c16', fontSize: '10px' }} />
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    {guests}
                  </Text>
                </div>
              </div>
            );
          }}
        />
        <Table.Column
          title="Betrag"
          width={120}
          align="right"
          sorter={(a: any, b: any) => (a.totalAmount || 0) - (b.totalAmount || 0)}
          dataIndex="totalAmount"
          render={(_, record: IInvoice) => {
            const total = getTotalAmount(record);
            return (
              <Text strong style={{ 
                color: total < 0 ? '#ff4d4f' : '#000',
                fontSize: '14px'
              }}>
                {formatPrice(total)}
              </Text>
            );
          }}
        />
        <Table.Column
          dataIndex="payment_status"
          title="Zahlung"
          width={100}
          render={(value) => getPaymentStatusTag(value)}
          filters={[
            { text: "Bezahlt", value: "paid" },
            { text: "Angezahlt", value: "partial" },
            { text: "Offen", value: "unpaid" },
          ]}
          onFilter={(value, record: any) => {
            const actualStatus = record.payment_status || 'unpaid';
            console.log('Payment Filter Debug - Value:', value, 'Record Status:', record.payment_status, 'Actual Status:', actualStatus);
            
            // Behandle verschiedene mögliche Status-Werte
            if (value === 'unpaid') {
              return actualStatus === 'unpaid' || actualStatus === 'pending' || !actualStatus || actualStatus === '';
            }
            
            return actualStatus === value;
          }}
        />
        <Table.Column
          dataIndex="email_sent"
          title="E-Mail"
          width={120}
          render={(_, record: IInvoice) => (
            <Space direction="vertical" size="small">
              {getEmailStatusTag(record)}
              <Button
                size="small"
                type="link"
                icon={<MailOutlined />}
                onClick={() => resendEmail(record)}
                style={{ padding: 0, height: 'auto' }}
              >
                {record.email_sent ? 'Erneut senden' : 'Senden'}
              </Button>
            </Space>
          )}
        />
        <Table.Column
          title="Aktionen"
          width={160}
          dataIndex="actions"
          render={(_, record: IInvoice) => (
            <Space>
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => navigateWithHash(`/rechnungen/anzeigen/${record.id}`)}
                title="Anzeigen"
              />
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigateWithHash(`/rechnungen/bearbeiten/${record.id}`)}
                title="Bearbeiten"
              />
              <Button
                size="small"
                icon={<FilePdfOutlined />}
                onClick={() => downloadPdf(record)}
                title="PDF herunterladen"
              />
              <Popconfirm
                title="Rechnung löschen?"
                description="Sind Sie sicher, dass Sie diese Rechnung löschen möchten?"
                onConfirm={() => {
                  // Lösch-Logik
                  message.success('Rechnung wurde gelöscht');
                }}
                okText="Ja"
                cancelText="Nein"
              >
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  title="Löschen"
                />
              </Popconfirm>
            </Space>
          )}
        />
      </Table>
    </List>
  );
}; 